import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaSchool, FaPlus, FaTrash } from "react-icons/fa";
import styles from "./Create.module.css";
import {
  CustomButton,
  CustomCheckbox,
  CustomDropdown,
  CustomTextInput,
} from "../../../../components";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const ClassCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { classId, className, sectionId, isView } = location.state || {};

  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const user_id = user?.id || "";

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    className: className || "",
    section: "",
    medium: "",
    shiftType: "",
    classTeacher: "",
    group: null,
    max_count: "",
  });

  const [subjectRows, setSubjectRows] = useState([
    {
      id: "",
      indexId: 1,
      subject: "",
      subjectTeacher: "",
      subjectTeacherOptions: [],
      loadingTeachers: false,
      examType: [],
    },
  ]);

  const [errors, setErrors] = useState({});
  const [subjectErrors, setSubjectErrors] = useState({});

  const [classTeacherOptions, setClassTeacherOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [shiftTypeOptions, setShiftTypeOptions] = useState([]);

  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  const mediumOptions = [
    { label: "Tamil", value: "tamil" },
    { label: "English", value: "english" },
    { label: "Hindi", value: "hindi" },
  ];

  const examTypeOptions = [
    { value: "theory", label: "Theory", disabled: false },
    { value: "practical", label: "Practical", disabled: false },
  ];

  useEffect(() => {
    fetchClassTeachers();
    fetchGroups();
    fetchSubjects();
    fetchShiftList();
  }, [school_id]);

  useEffect(() => {
    if (sectionId) {
      fetchClassDetails(sectionId);
    }
  }, [sectionId]);

  const fetchShiftList = async () => {
    try {
      const response = await AxiosInstance.get(
        `/school/shiftList/${school_id}`,
      );
      if (response?.data?.status) {
        const options = (response.data.data || []).map((shift) => ({
          label: shift.shift_type || "N/A",
          value: shift.id.toString(),
        }));
        setShiftTypeOptions(options);
      } else {
        setShiftTypeOptions([]);
      }
    } catch (error) {
      console.error("Error fetching shift list:", error);
      setShiftTypeOptions([]);
    }
  };

  const fetchClassDetails = async (id) => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(`/school/classView/${id}`);

      if (response?.data?.status && response?.data?.data?.length > 0) {
        const classData = response.data.data[0];

        setFormData({
          className: className || "",
          section: classData.section_id || "",
          medium: classData.medium
            ? mediumOptions.find((opt) => opt.label === classData.medium) || ""
            : "",
          shiftType: classData.shift_type
            ? classData.shift_type.toString()
            : "",
          classTeacher:
            classData.class_staff_id && classData.class_staff
              ? {
                label: classData.class_staff,
                value: classData.class_staff_id.toString(),
              }
              : "",
          group:
            classData.group_id && classData.group_name
              ? {
                label: classData.group_name,
                value: classData.group_id.toString(),
              }
              : null,
          max_count: classData.max_count ? classData.max_count.toString() : "",
        });

        if (classData.subject && classData.subject.length > 0) {
          const loadedRows = classData.subject.map((sub, index) => {
            const examType = [];
            if (sub.theory === 1) examType.push("theory");
            if (sub.practical === 1) examType.push("practical");

            // Check if subject_teacher name exists and is not null/empty
            const hasValidTeacherName =
              sub.subject_teacher && sub.subject_teacher.trim() !== "";

            const teacherObj =
              sub.teaching_id && hasValidTeacherName
                ? {
                  label: sub.subject_teacher,
                  value: sub.teaching_id.toString(),
                }
                : ""; // Keep empty string if subject_teacher is null or empty

            return {
              id: sub.id,
              indexId: index + 1,
              subject:
                sub.subject_id && sub.subject_name
                  ? {
                    label: sub.subject_name,
                    value: sub.subject_id.toString(),
                  }
                  : "",
              targetTeachingId: sub.teaching_id ? sub.teaching_id.toString() : null,
              subjectTeacher: teacherObj,
              subjectTeacherOptions: teacherObj ? [teacherObj] : [],
              loadingTeachers: false,
              examType,
            };
          });

          setSubjectRows(loadedRows);

          loadedRows.forEach((row) => {
            if (row.subject?.value) {
              fetchStaffBySubject(row.subject.value, row.indexId);
            }
          });
        }
      }
    } catch (error) {
      console.error("Error fetching class details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassTeachers = async () => {
    try {
      const response = await AxiosInstance.post("/school/staff/list", {
        id: school_id,
        search: "",
        page: "",
        limit: "",
      });

      if (response?.data?.status) {
        const options = (response.data.data || []).map((item) => ({
          label: item.name,
          value: item.id.toString(),
        }));
        setClassTeacherOptions(options);
      } else {
        setClassTeacherOptions([]);
      }
    } catch (error) {
      console.error("Error fetching class teachers:", error);
      setClassTeacherOptions([]);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await AxiosInstance.post("/school/listGroup", {
        page: "",
        limit: "",
        search: "",
      });

      if (response?.data?.rows) {
        const options = (response.data.rows || []).map((item) => ({
          label: item.name,
          value: item.id.toString(),
        }));
        setGroupOptions(options);
      } else {
        setGroupOptions([]);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      setGroupOptions([]);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await AxiosInstance.post("/school/listSubject", {
        page: "",
        limit: "",
        search: "",
      });

      if (response?.data?.rows) {
        const options = (response.data.rows || []).map((item) => ({
          label: item.subject_name,
          value: item.id.toString(),
        }));
        setSubjectOptions(options);
      } else {
        setSubjectOptions([]);
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setSubjectOptions([]);
    }
  };

  const fetchStaffBySubject = async (subjectId, rowIndexId) => {
    setSubjectRows((prev) =>
      prev.map((row) =>
        row.indexId === rowIndexId ? { ...row, loadingTeachers: true } : row,
      ),
    );

    try {
      const response = await AxiosInstance.post("/school/staff/by-subject", {
        subject_id: parseInt(subjectId),
        school_id: school_id,
      });

      if (response?.data?.success) {
        const options = (response.data.data || []).map((item) => ({
          label: item.name,
          value: item.id.toString(),
        }));

        setSubjectRows((prev) =>
          prev.map((row) => {
            if (row.indexId !== rowIndexId) return row;

            const targetId =
              row.targetTeachingId ||
              (row.subjectTeacher?.value
                ? row.subjectTeacher.value
                : row.subjectTeacher?.toString());

            // Search options for a matching teacher ID
            const matchedTeacher = options.find((opt) => opt.value === targetId);

            const hasExistingLabel =
              row.subjectTeacher &&
              typeof row.subjectTeacher === "object" &&
              row.subjectTeacher.label &&
              row.subjectTeacher.label.trim() !== "";

            return {
              ...row,
              subjectTeacherOptions: options,
              // Use matched teacher object if found; otherwise, reset to ""
              subjectTeacher: matchedTeacher
                ? matchedTeacher
                : hasExistingLabel
                  ? row.subjectTeacher
                  : "",
              loadingTeachers: false,
            };
          }),
        );
      } else {
        setSubjectRows((prev) =>
          prev.map((row) =>
            row.indexId === rowIndexId
              ? { ...row, subjectTeacherOptions: [], subjectTeacher: "", loadingTeachers: false }
              : row,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching staff by subject:", error);
      setSubjectRows((prev) =>
        prev.map((row) =>
          row.indexId === rowIndexId
            ? { ...row, subjectTeacherOptions: [], subjectTeacher: "", loadingTeachers: false }
            : row,
        ),
      );
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // max_count: allow only numbers, max 3 digits
    if (name === "max_count") {
      if (value !== "" && !/^\d{0,3}$/.test(value)) return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubjectRowChange = (rowIndexId, field, value) => {
    if (field === "subject" && value) {
      const subjectValue = value?.value || value;
      const isAlreadySelected = subjectRows.some(
        (row) =>
          row.indexId !== rowIndexId &&
          (row.subject?.value || row.subject) === subjectValue,
      );

      if (isAlreadySelected) {
        setSubjectErrors((prev) => ({
          ...prev,
          [`${rowIndexId}-subject`]:
            "This subject has already been assigned. Please select a different subject.",
        }));
        return;
      }

      fetchStaffBySubject(value?.value || value, rowIndexId);

      setSubjectRows((prev) =>
        prev.map((row) =>
          row.indexId === rowIndexId
            ? {
              ...row,
              subject: value,
              subjectTeacher: "",
              subjectTeacherOptions: [],
            }
            : row,
        ),
      );
    } else {
      setSubjectRows((prev) =>
        prev.map((row) =>
          row.indexId === rowIndexId ? { ...row, [field]: value } : row,
        ),
      );
    }

    if (subjectErrors[`${rowIndexId}-${field}`]) {
      setSubjectErrors((prev) => ({
        ...prev,
        [`${rowIndexId}-${field}`]: "",
      }));
    }
  };

  const handleExamTypeChange = (rowIndexId, selectedValues) => {
    setSubjectRows((prev) =>
      prev.map((row) =>
        row.indexId === rowIndexId ? { ...row, examType: selectedValues } : row,
      ),
    );

    if (subjectErrors[`${rowIndexId}-examType`]) {
      setSubjectErrors((prev) => ({
        ...prev,
        [`${rowIndexId}-examType`]: "",
      }));
    }
  };

  const handleAddSubjectRow = () => {
    const newIndexId = Math.max(...subjectRows.map((r) => r.indexId), 0) + 1;
    setSubjectRows((prev) => [
      ...prev,
      {
        id: "",
        indexId: newIndexId,
        subject: "",
        subjectTeacher: "",
        subjectTeacherOptions: [],
        loadingTeachers: false,
        examType: [],
      },
    ]);
  };

  const handleRemoveSubjectRow = (rowIndexId) => {
    if (subjectRows.length === 1) return;
    setSubjectRows((prev) => prev.filter((row) => row.indexId !== rowIndexId));

    setSubjectErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`${rowIndexId}-subject`];
      delete newErrors[`${rowIndexId}-subjectTeacher`];
      delete newErrors[`${rowIndexId}-examType`];
      return newErrors;
    });
  };

  const getAvailableSubjects = (currentRowIndexId) => {
    const selectedSubjects = subjectRows
      .filter((row) => row.indexId !== currentRowIndexId && row.subject)
      .map((row) => row.subject?.value || row.subject);

    return subjectOptions.filter(
      (option) => !selectedSubjects.includes(option.value),
    );
  };

  const validateForm = () => {
    const newErrors = {};
    const newSubjectErrors = {};

    if (!formData.section?.trim()) {
      newErrors.section = "Section is required";
    }
    if (!formData.medium) {
      newErrors.medium = "Medium is required";
    }
    if (!formData.shiftType) {
      newErrors.shiftType = "Shift Type is required";
    }
    if (!formData.classTeacher) {
      newErrors.classTeacher = "Class Teacher is required";
    }
    if (
      (formData.className === "XII" || formData.className === "XI") &&
      !formData.group
    ) {
      newErrors.group = "Group is required";
    }

    // max_count validation
    if (!formData.max_count?.toString().trim()) {
      newErrors.max_count = "Max Count is required";
    } else if (!/^\d{1,3}$/.test(formData.max_count.toString().trim())) {
      newErrors.max_count = "Enter a valid number (max 3 digits)";
    } else if (parseInt(formData.max_count) <= 0) {
      newErrors.max_count = "Max Count must be greater than 0";
    }

    subjectRows.forEach((row) => {
      if (!row.subject) {
        newSubjectErrors[`${row.indexId}-subject`] = "Subject is required";
      }
      if (!row.subjectTeacher) {
        newSubjectErrors[`${row.indexId}-subjectTeacher`] =
          "Subject Teacher is required";
      }
      if (!row.examType || row.examType.length === 0) {
        newSubjectErrors[`${row.indexId}-examType`] =
          "Please select at least Theory or Practical";
      }
    });

    const selectedSubjects = subjectRows
      .map((row) => row.subject?.value || row.subject)
      .filter(Boolean);
    const duplicates = selectedSubjects.filter(
      (item, index) => selectedSubjects.indexOf(item) !== index,
    );

    if (duplicates.length > 0) {
      subjectRows.forEach((row) => {
        const subjectValue = row.subject?.value || row.subject;
        if (duplicates.includes(subjectValue)) {
          newSubjectErrors[`${row.indexId}-subject`] =
            "Duplicate subject selected";
        }
      });
    }

    setErrors(newErrors);
    setSubjectErrors(newSubjectErrors);

    return (
      Object.keys(newErrors).length === 0 &&
      Object.keys(newSubjectErrors).length === 0
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      let response;

      if (sectionId) {
        const payload = {
          id: sectionId,
          client_id: client_id,
          school_id: school_id,
          class_id: classId,
          section_id: formData.section,
          medium: formData.medium?.value || formData.medium,
          shift_type: formData.shiftType?.value
            ? parseInt(formData.shiftType.value)
            : formData.shiftType
              ? parseInt(formData.shiftType)
              : null,
          group_id: parseInt(formData.group?.value || null),
          class_staff: parseInt(
            formData.classTeacher?.value || formData.classTeacher,
          ),
          max_count: parseInt(formData.max_count),
          subject: subjectRows.map((row) => ({
            id: row.id || "",
            subject_id: parseInt(row.subject?.value || row.subject),
            teaching_id: parseInt(
              row.subjectTeacher?.value || row.subjectTeacher,
            ),
            theory: row.examType.includes("theory") ? 1 : 0,
            practical: row.examType.includes("practical") ? 1 : 0,
          })),
          updated_by: user_id,
        };

        response = await AxiosInstance.put("/school/classUpdate", payload);
      } else {
        const payload = {
          client_id: client_id,
          school_id: school_id,
          class_id: classId,
          section_id: formData.section,
          medium: formData.medium?.value || formData.medium,
          shift_type: formData.shiftType?.value
            ? parseInt(formData.shiftType.value)
            : formData.shiftType
              ? parseInt(formData.shiftType)
              : null,
          group_id: parseInt(formData.group?.value || null),
          class_staff: parseInt(
            formData.classTeacher?.value || formData.classTeacher,
          ),
          max_count: parseInt(formData.max_count),
          subject: subjectRows.map((row) => ({
            subject_id: parseInt(row.subject?.value || row.subject),
            teaching_id: parseInt(
              row.subjectTeacher?.value || row.subjectTeacher,
            ),
            theory: row.examType.includes("theory") ? 1 : 0,
            practical: row.examType.includes("practical") ? 1 : 0,
          })),
          created_by: user_id,
        };

        response = await AxiosInstance.post("/school/classCreate", payload);
      }

      if (response?.data?.status || response?.data?.success) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message || sectionId
              ? "Class section updated successfully!"
              : "Class section created successfully!",
          redirectPath: {
            path: "/user/classSectionList",
            state: { classId, className },
          },
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response?.data?.message || "Something went wrong!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          "Failed to save class section. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/user/classSectionList", {
      state: { classId, className },
    });
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaSchool />
          </div>
          <h1 className={styles.headerTitle}>Class Configuration</h1>
        </div>
        <div className={styles.loadingContainer}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const handleSuccessOk = () => {
    setModalState((prev) => ({ ...prev, open: false }));
    if (modalState.type === "success" && modalState.redirectPath) {
      navigate(modalState.redirectPath.path, {
        state: modalState.redirectPath.state,
      });
    }
  };

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaSchool />
          </div>
          <h1 className={styles.headerTitle}>
            {isView
              ? "View Class Section"
              : sectionId
                ? "Edit Class Section"
                : "Create Class Section"}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Section 1: Basic Details */}
          <div className={styles.sectionTitle}>Basic Details</div>
          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Class Name"
                name="className"
                type="text"
                value={formData.className}
                onChange={handleInputChange}
                placeholder="Class Name"
                required
                disabled={true}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Section"
                name="section"
                type="text"
                value={formData.section}
                onChange={handleInputChange}
                placeholder="E.g. A, B, C"
                required
                error={errors.section}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label="Medium"
                name="medium"
                value={formData.medium}
                onChange={(value) => handleDropdownChange("medium", value)}
                options={mediumOptions}
                placeholder="Select Medium"
                required
                error={errors.medium}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label="Shift Type"
                name="shiftType"
                value={formData.shiftType}
                onChange={(value) => handleDropdownChange("shiftType", value)}
                options={shiftTypeOptions}
                placeholder="Select Shift Type"
                required
                error={errors.shiftType}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label="Class Teacher"
                name="classTeacher"
                value={formData.classTeacher}
                onChange={(value) =>
                  handleDropdownChange("classTeacher", value)
                }
                options={classTeacherOptions}
                placeholder="Select Class Teacher"
                required
                error={errors.classTeacher}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label="Max Count"
                name="max_count"
                type="text"
                value={formData.max_count}
                onChange={handleInputChange}
                placeholder="E.g. 60"
                required
                error={errors.max_count}
                maxLength={3}
              />
            </div>

            {(formData.className === "XII" || formData.className === "XI") && (
              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label="Group"
                  name="group"
                  value={formData.group}
                  onChange={(value) => handleDropdownChange("group", value)}
                  options={groupOptions}
                  placeholder="Select Group"
                  required
                  error={errors.group}
                />
              </div>
            )}
          </div>

          {/* Section 2: Subject Assignment */}
          <div className={styles.sectionTitle} style={{ marginTop: "24px" }}>
            Subject Assignment
          </div>

          {subjectRows.map((row, index) => (
            <div className="row" key={row.indexId}>
              {/* Subject */}
              <div className="col-lg-3 px-2 col-md-4 col-sm-12">
                <CustomDropdown
                  label={index === 0 ? "Subject" : ""}
                  name={`subject-${row.indexId}`}
                  value={row.subject}
                  onChange={(value) =>
                    handleSubjectRowChange(row.indexId, "subject", value)
                  }
                  options={getAvailableSubjects(row.indexId)}
                  placeholder="Select Subject"
                  required
                  error={subjectErrors[`${row.indexId}-subject`]}
                />
              </div>

              {/* Subject Teacher */}
              <div className="col-lg-3 px-2 col-md-4 col-sm-12">
                <CustomDropdown
                  label={index === 0 ? "Subject Teacher" : ""}
                  name={`subjectTeacher-${row.indexId}`}
                  value={row.subjectTeacher}
                  onChange={(value) =>
                    handleSubjectRowChange(row.indexId, "subjectTeacher", value)
                  }
                  options={row.subjectTeacherOptions}
                  placeholder={
                    row.loadingTeachers
                      ? "Loading..."
                      : !row.subject
                        ? "Select Subject First"
                        : row.subjectTeacherOptions.length === 0
                          ? "No Teachers Available"
                          : "Select Subject Teacher"
                  }
                  required
                  error={subjectErrors[`${row.indexId}-subjectTeacher`]}
                />
              </div>

              {/* Exam Type Checkboxes */}
              <div className="col-lg-3 px-2 col-md-3 col-sm-12">
                <CustomCheckbox
                  label={index === 0 ? "Exam Type" : ""}
                  name={`examType-${row.indexId}`}
                  value={row.examType}
                  onChange={(selectedValues) =>
                    handleExamTypeChange(row.indexId, selectedValues)
                  }
                  options={examTypeOptions}
                  required
                  error={subjectErrors[`${row.indexId}-examType`]}
                  inline
                />
              </div>

              {/* Add/Remove Buttons */}
              <div className="col-lg-3 px-2 col-md-1 col-sm-12 mb-3">
                <div style={{ display: "flex", gap: "8px" }}>
                  {index === subjectRows.length - 1 && (
                    <button
                      type="button"
                      className={styles.addRowBtn}
                      onClick={handleAddSubjectRow}
                      title="Add Subject"
                    >
                      <FaPlus />
                    </button>
                  )}
                  {subjectRows.length > 1 && (
                    <button
                      type="button"
                      className={styles.removeRowBtn}
                      onClick={() => handleRemoveSubjectRow(row.indexId)}
                      title="Remove Subject"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {isView ? "BACK" : "CANCEL"}
            </CustomButton>

            <CustomButton
              type="submit"
              variant="contained"
              className={styles.submitButton}
              fullWidth={false}
              loading={submitting}
              loadingText={sectionId ? "Updating..." : "Submitting..."}
            >
              {sectionId ? "UPDATE" : "SUBMIT"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default ClassCreate;
